"use client";

import CustomButton from "@/components/common/button";
import Cards from "@/components/content/cards";
import ContentSuggestionsRail from "@/components/content/ContentSuggestionsRail";
import Comments from "@/components/content/comments";
import { Card, CardBody } from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { ROUTES } from "@/routes/appRoutes";
import UserReport from "@/components/content/userReport";
import LivePlay from "@/helpers/svg/livePlay.svg";
import ContentActions from "@/components/content/contentActions";
import { mergeArtistDuplicates } from "@/helpers/data.utils";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import axiosInstance from "@/config/axiosInstance";
import { getCMSApiRoute } from "@/helpers/api.util";
import { useSelector } from "react-redux";
import PreviewModal from "@/components/content/previewModal";
import { getPathname } from "@/helpers/browser.utils";
import styled from "styled-components";
import { useContentViewAnalytics } from "@/hooks/useMediaAnalytics";
import {
  buildContentAnalyticsPayload,
  sendPlayIntent,
} from "@/services/mediaAnalyticsService";
import DetailHeroScene from "@/components/common/DetailHeroScene";
import LiveStartCountdown from "@/components/common/LiveStartCountdown";
import { toPersianDigits } from "@/utils/liveStartCountdown";
import { isComingSoon } from "@/utils/contentStatus";
import {
  getServerNow,
  subscribePublicAuthConfig,
} from "@/services/publicAuthConfigService";

const CountdownOuterWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 72px; /* same as play/action buttons */
  height: auto;
  margin-left: 12px; /* exact same spacing as other action buttons */
`;

const CountdownLabel = styled.div`
  position: static;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #e2e8f0;
  font-size: 20px;
  font-weight: 600;
  font-family: "AnjomanMax", sans-serif;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6); /* slight shadow for contrast */
  white-space: nowrap;
`;

const LivePulseDot = styled.span`
  width: 10px;
  height: 10px;
  background-color: #ef4444;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 8px #ef4444;
  animation: pulseLive 1.5s infinite;
  @keyframes pulseLive {
    0% {
      transform: scale(0.9);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    }
    70% {
      transform: scale(1.1);
      box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
    }
    100% {
      transform: scale(0.9);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }
`;

const LiveDetail = ({ data, isAdminPreview = false }) => {
  const router = useRouter();
  useContentViewAnalytics(data, {
    enabled: !isAdminPreview,
    source: "live_detail",
  });
  const { canPlay, canPlayLoading } = useSubscriptionStatus();
  const { isLogin } = useSelector((state) => state.Auth?.profile);

  const [isMounted, setIsMounted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [redirectSecondsLeft, setRedirectSecondsLeft] = useState(null);

  data = data || { deleted: true };
  const comingSoon = isComingSoon(data.status);

  const liveStartDate = data?.liveInfo?.liveStartDate;

  const { artists } = data;
  const artistList = artists?.reduce((acc, i) => {
    const { farsiName, image, _id, slug } = i.artist?.meta?.bin || {};
    return acc.concat({
      artist: {
        farsiName,
        profession: i.profession?.name,
        contentPhotos: [{ type: "banner", src: image }],
        id: _id,
        slug,
        priority: i.priority ?? i.artist?.priority,
      },
    });
  }, []);

  const reportContentWatch = async () => {
    try {
      await axiosInstance.post(getCMSApiRoute("action/watch"), {
        contentKey: data.slug,
        isEpisode: 0,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getPlayUrl = () => {
    return ROUTES.player + "/" + data.slug;
  };

  const reportPlayIntent = () => {
    const payload = buildContentAnalyticsPayload(data, {
      surface: "content_detail",
      source: "live_play_button",
    });
    if (payload) sendPlayIntent(payload);
  };

  const handleWatchEvent = async (data) => {
    if (isComingSoon(data?.status)) return;
    try {
      if (data.isFree === 1) {
        if (!isLogin) {
          return router.push(
            `/auth/login?ref=${encodeURIComponent(getPlayUrl())}`
          );
        } else {
          const playUrl = getPlayUrl();
          await reportContentWatch();
          reportPlayIntent();
          router.push(playUrl);
        }
      } else {
        if (!isLogin) {
          return router.push(
            `/auth/login?ref=${encodeURIComponent(getPathname())}`
          );
        } else {
          if (!canPlay) {
            return router.push("/subscription");
          } else {
            const playUrl = getPlayUrl();
            await reportContentWatch();
            reportPlayIntent();
            router.push(playUrl);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getWatchLabel = () => {
    if (!isLogin) {
      return "ورود و تماشا";
    } else if (data.isFree === 1 || canPlay) {
      return "تماشا کن";
    } else {
      return "خرید اشتراک و تماشا";
    }
  };

  const handleLoginRedirect = () => {
    router.push(`/auth/login?ref=${encodeURIComponent(getPathname())}`);
  };

  useEffect(() => {
    setIsMounted(true);
    if (comingSoon || !liveStartDate) {
      setTimeLeft(null);
      setRedirectSecondsLeft(null);
      return;
    }

    let hadActiveCountdown = false;
    let timer = null;

    const clearCountdownTimer = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const calculateTimeLeft = () => {
      const difference = liveStartDate - getServerNow();
      if (difference <= 0) return null;

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    const updateCountdown = () => {
      const calculated = calculateTimeLeft();

      if (calculated) {
        hadActiveCountdown = true;
        setTimeLeft(calculated);
        setRedirectSecondsLeft(null);

        if (!timer) {
          timer = setInterval(updateCountdown, 1000);
        }
        return;
      }

      setTimeLeft(calculated);
      clearCountdownTimer();

      if (hadActiveCountdown) {
        setRedirectSecondsLeft(5);
      }
    };

    updateCountdown();
    const unsubscribe = subscribePublicAuthConfig(updateCountdown);

    return () => {
      clearCountdownTimer();
      unsubscribe();
    };
  }, [liveStartDate, comingSoon]);

  useEffect(() => {
    if (comingSoon) return;
    if (redirectSecondsLeft === null) return;

    if (redirectSecondsLeft > 0) {
      const redirectTimer = setTimeout(() => {
        setRedirectSecondsLeft((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(redirectTimer);
    } else if (redirectSecondsLeft === 0) {
      handleWatchEvent(data);
    }
  }, [redirectSecondsLeft, comingSoon]);

  if (!data) {
    return (
      <div className="min-h-[100vh] flex justify-center items-center">
        <Card className="dark p-10">
          <CardBody>محتوای مورد نظر یافت نشد</CardBody>
        </Card>
      </div>
    );
  }

  const renderLiveButtons = () => (
    <>
      {comingSoon ? (
        <span className="inline-flex min-h-11 items-center rounded-full border border-white/30 bg-white/10 px-5 font-semibold text-white">
          فعلاً قابل تماشا نیست
        </span>
      ) : isMounted && timeLeft ? (
        <>
          <CountdownOuterWrapper className="content-detail-play-wrapper content-detail-live-countdown">
            <LiveStartCountdown
              startTimestamp={liveStartDate}
              size="detail"
              className="justify-center"
            />
          </CountdownOuterWrapper>
          {!isLogin && (
            <CustomButton
              label="ورود"
              classNames="content-detail-login-button bg-[#262626] !ml-0"
              textColor="text-primary-white"
              onClick={handleLoginRedirect}
            />
          )}
        </>
      ) : (
        <CountdownOuterWrapper className="content-detail-play-wrapper">
          {isMounted &&
            redirectSecondsLeft !== null &&
            redirectSecondsLeft > 0 && (
              <CountdownLabel>
                <LivePulseDot />
                انتقال به پخش در {toPersianDigits(redirectSecondsLeft)} ثانیه...
              </CountdownLabel>
            )}
          <CustomButton
            label={getWatchLabel()}
            classNames="content-detail-play-button bg-[#18731C] !ml-0"
            textColor="text-primary-white"
            rightIcon={<LivePlay />}
            onClick={() => handleWatchEvent(data)}
            isLoading={canPlayLoading}
            showLabelOnLoading={false}
          />
        </CountdownOuterWrapper>
      )}
      <PreviewModal src={data.trailer} />
      <ContentActions contentKey={data.slug} />
      <UserReport contentKey={data.slug} data={data} />
    </>
  );

  return (
    <div className="h-auto min-h-screen">
      <DetailHeroScene
        content={{
          ...data,
          isLive: true,
          titleEn: data.titleEN,
          imdbRate: data.imdb_rate || data.imdbRate,
        }}
        renderButtons={renderLiveButtons}
      />
      {artistList?.length > 0 && (
        <div className="lg:py-20 md:py-12 py-6 md:container mx-auto mb-5 relative z-10 px-5 2xl:px-0 xlg:max-w-[1920px]">
          <Cards
            items={mergeArtistDuplicates(artistList)}
            shape="square"
            isArtist={true}
            isSwiper={true}
            noTag={true}
            categoryName="هنرمندان"
            showTitle={true}
            type="artist"
            noContainer={true}
          />
        </div>
      )}
      <ContentSuggestionsRail
        slug={data.slug}
        type="movie"
        contentKind="live"
        enabled={!isAdminPreview}
      />
      <div className="py-10"></div>
      <div className="mt-10 mx-auto w-full md:container xlg:max-w-[1920px]">
        <Comments contentKey={data.slug} />
      </div>
    </div>
  );
};

export default LiveDetail;
